import { cloneDeep } from 'lodash-es';
import { getConfig } from '../config';
import { getImageMattingResult } from '../matting';
import { getImageBaseMask } from '../matting/api';
import { base64ToFile } from '../utils/image';
import { queryDeliverResults, retouchingDeliver } from './api';
import type { DeliverParams, LoopParams, RetouchingBatchParams, RetouchingBatchTask } from './type';

export async function retouching(params: DeliverParams): Promise<string> {
    const { originalImageURL, maskURL, subfunc, transfer, fromDemo, transferID, v2Params } = params;

    return new Promise((resolve, reject) => {
        const onQuery = async (tasks: RetouchingBatchTask[]) => {
            if (tasks.length === 0) return;
            const task = tasks[0];
            if (task.status === 'pending') return;
            if (task.status === 'failed') return reject();
            if (!task.resultImageURL) return reject();

            let result = task.resultImageURL;
            // 但是如果原图存在透明背景
            // 算法不管走的是抠图精修还是保留背景，都会把透明背景变成白色
            // 所以需要对结果图再走一次抠图
            const hasTransparent = await hasTransparentBackground(originalImageURL);
            if (hasTransparent) {
                const { uploadFileToObs } = getConfig();
                const mattingResult = await getMattingImage(result);
                if (mattingResult && uploadFileToObs) {
                    const file = base64ToFile(mattingResult, '');
                    const res = await uploadFileToObs(file);
                    result = res.url;
                }
            }
            resolve(result);
        };

        const onQueryFailed = (message?: string) => {
            reject(message);
        };

        retouchingBatch({
            taskParams: [
                {
                    originalImageURL,
                    maskURL,
                },
            ],
            transfer,
            transferID,
            subfunc,
            onQuery: onQuery,
            onQueryFailed: onQueryFailed,
            onQuerySuccess: () => {},
            fromDemo,
            v2Params,
        });
    });
}

export async function retouchingBatch(params: RetouchingBatchParams) {
    const { taskParams, transfer, subfunc, onQuery, onQueryFailed, onQuerySuccess, fromDemo, transferID, v2Params } =
        params;

    try {
        const tasks: RetouchingBatchTask[] = taskParams.map((p) => {
            return {
                originalImageURL: p.originalImageURL,
                status: 'pending',
            };
        });

        const promise = taskParams.map(async (p) => {
            return retouchingDeliver({
                originalImageURL: p.originalImageURL,
                maskURL: p.maskURL,
                subfunc,
                transfer,
                transferID,
                fromDemo,
                v2Params,
            });
        });

        const ids = await Promise.all(promise);
        ids.forEach((id, index) => {
            tasks[index].taskId = id;
        });
        if (ids.length > 0) {
            loop({ tasks, ids, onQuery, onQuerySuccess });
        } else {
            onQueryFailed();
        }
    } catch (error: any) {
        onQueryFailed(error.message);
    }
}

const loop = async (params: LoopParams) => {
    const { tasks, ids, onQuery, onQuerySuccess } = params;
    const results = await pollImageResult(tasks, ids);
    onQuery(results);
    if (results.every((r) => r.status !== 'pending')) {
        onQuerySuccess(results);
    } else {
        const leftIds = results.filter((r) => r.status === 'pending').map((r) => r.taskId!);
        setTimeout(() => {
            loop({ tasks: results, ids: leftIds, onQuery, onQuerySuccess });
        }, 1000);
    }
};

async function pollImageResult(t: RetouchingBatchTask[], ids: string[]): Promise<RetouchingBatchTask[]> {
    const tasks: RetouchingBatchTask[] = cloneDeep(t);

    const results = await queryDeliverResults(ids);

    for (const d of results) {
        const index = tasks.findIndex((w) => w.taskId === d.msg_id);
        if (index === -1) continue;
        if (tasks[index].status !== 'pending') continue;

        if (d.err_code === 0) {
            // 成功
            const info = d.media_info_list ? d.media_info_list[0] : null;
            if (!info) {
                tasks[index] = {
                    ...tasks[index],
                    status: 'failed',
                };
                continue;
            }
            tasks[index] = {
                ...tasks[index],
                resultImageURL: info.media_data,
                status: 'success',
            };
        } else if (d.err_code !== 29901) {
            // 失败
            tasks[index] = {
                ...tasks[index],
                status: 'failed',
            };
        }
    }

    return tasks;
}

// 判断图片是否存在透明背景
async function hasTransparentBackground(url: string): Promise<boolean> {
    const img = await urlToImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, img.width, img.height);
    let transparentPixelCount = 0;
    for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha === 0) {
            transparentPixelCount++;
        }
    }
    return transparentPixelCount > 20;
}

async function urlToImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
            resolve(img);
        };
        img.onerror = (e) => {
            reject(e);
        };
        img.src = url;
    });
}

async function getMattingImage(url: string) {
    const maskResult = await getImageBaseMask({
        isUseNewAPI: true,
        params: {
            imageURL: url,
            keepResolution: true,
        },
    });
    const [maskImg, resultImg] = await Promise.all([urlToImage(maskResult.maskURL), urlToImage(url)]);

    const mattingResult = await getImageMattingResult({
        width: maskImg.width,
        height: maskImg.height,
        image: resultImg,
        maskImage: maskImg,
        useBlurFilterRun: true,
    });

    return mattingResult?.toDataURL();
}
